import { AsyncPipe, NgClass } from '@angular/common';
import { Component, DOCUMENT, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { SuiOverlayOutletComponent } from '@semeris/ui';
import { isEqual } from 'lodash-es';
import { combineLatest, fromEvent, Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { TrayComponent } from '../../../tray/src/app/components/tray/tray.component';
import { ConversationSidebarComponent } from '../features/comment/containers/conversation-sidebar/conversation-sidebar.component';
import { ViewerCommand, XRayOptions } from '../features/document-viewer/models/document-viewer.models';
import { DocumentViewerService } from '../features/document-viewer/services/document-viewer.service';
import { HelpComponent } from '../features/help/components/help/help.component';
import { ActivityEvent } from '../features/home/models/activity-event.model';
import { ApplicationService } from '../features/home/services/application.service';
import { AggregatedLoggerService } from '../features/logging/services/aggregated-logger.service';
import { WARNING_NOTIFICATION_CONFIGURATION } from '../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../features/shared/constants/routes';
import { ClosableInputControllerDirective } from '../features/shared/directives/closable-input-controller.directive';
import { CopyToControllerDirective } from '../features/shared/directives/copy/copy-to-controller.directive';
import { ItemCartMediatorDirective } from '../features/shared/directives/item-cart-mediator.directive';
import { Permission } from '../features/shared/models/api/permissions.enum';
import { PanelCommand, PanelTarget } from '../features/shared/models/common/command';
import { WebSocketEntities } from '../features/shared/models/web-socket/web-socket.models';
import { DataService } from '../features/shared/services/data.service';
import { PermissionService } from '../features/shared/services/permission.service';
import { ThemeService } from '../features/shared/services/theme.service';
import { UpdateService } from '../features/shared/services/update.service';
import { SidenavComponent } from '../features/sidenav/components/sidenav/sidenav.component';
import { SystemMessageOutletComponent } from '../features/system-message/components/system-message-outlet/system-message-outlet.component';
import { SystemMessageType } from '../features/system-message/models/system-message.model';
import { ToolbarComponent } from '../features/toolbar/components/toolbar/toolbar.component';
import { IconButtonDirective } from '../features/ui/button/icon-button/icon-button.directive';
import { AppActions } from '../store/app/app.actions';
import { AppSelectors } from '../store/app/app.selectors';
import { TitleSelectors } from '../store/app/title.selectors';
import { DatapointLookupActions } from '../store/datapoint-lookup/datapoint-lookup.actions';
import { DealFilterActions } from '../store/deal-filter/deal-filter.actions';
import { DealFilterSelectors } from '../store/deal-filter/deal-filter.selectors';
import { DocumentViewerActions } from '../store/document-viewer/document-viewer.actions';
import { DocumentViewerSelectors } from '../store/document-viewer/document-viewer.selectors';
import { PanelActions } from '../store/panel/panel.actions';
import { PanelViewId } from '../store/panel/panel.state';
import { HomeActions } from '../store/home/home.actions';
import { LabelActions } from '../store/label/label.actions';
import { MenuActions } from '../store/menu/menu.actions';
import { RootState } from '../store/root-state.model';
import { RootStoreActions } from '../store/root-store.actions';
import { TransactionActions } from '../store/transaction/transaction.actions';
import { UserProfileActions } from '../store/user-profile/user-profile.actions';
import { UserProfileSelectors } from '../store/user-profile/user-profile.selectors';

@Component({
  selector: 'semeris-root',
  templateUrl: './app.component.html',
  styleUrls: [],
  imports: [
    ClosableInputControllerDirective,
    NgClass,
    RouterOutlet,
    SidenavComponent,
    ItemCartMediatorDirective,
    CopyToControllerDirective,
    ToolbarComponent,
    HelpComponent,
    ConversationSidebarComponent,
    TrayComponent,
    IconButtonDirective,
    MatIcon,
    AsyncPipe,
    SuiOverlayOutletComponent,
    SystemMessageOutletComponent,
  ],
  providers: [provideNativeDateAdapter()],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly logger = inject(AggregatedLoggerService);
  private readonly authService = inject(AuthService);
  private readonly dataService = inject(DataService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly permissionService = inject(PermissionService);
  private readonly applicationService = inject(ApplicationService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly documentViewerService = inject(DocumentViewerService);
  private readonly updateService = inject(UpdateService);

  public isAuthenticated$: Observable<boolean>;
  public isSmall$: Observable<boolean>;
  public breakPointId$: Observable<string>;
  public isEmbedded$: Observable<boolean> = this.store.select(AppSelectors.isEmbedded);
  public appEnabled$: Observable<boolean> = this.store.select(AppSelectors.appEnabled);

  public readonly SystemMessageType = SystemMessageType;

  @HostListener('window:beforeprint')
  public onBeforePrint() {
    this.store.dispatch(AppActions.SetPrintMode({ isPrintMode: true }));
    this.store.dispatch(PanelActions.SetCalculatedViewerState({ state: [PanelViewId.Summary] }));
  }

  @HostListener('window:afterprint')
  public onAfterPrint() {
    this.store.dispatch(AppActions.SetPrintMode({ isPrintMode: false }));
  }

  @HostListener('window:visibilitychange')
  public onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.initHeartbeat(0);
      this.updateService.checkForUpdate();
    } else {
      this.pauseHeartbeat();
    }
  }

  public helpOpen$ = this.store.select(AppSelectors.helpOpen);
  public helpMode$ = this.store.select(AppSelectors.helpMode);
  public conversationSidebarOpen$ = this.store.select(AppSelectors.conversationSidebarOpen).pipe(delay(0));

  private heartBeatSubscription: Subscription;
  private readonly subscription$ = new Subscription();

  private xRayOptionsLookup: Record<XRayOptions, string> = {
    [XRayOptions.Entities]: 'entitiesEnabled',
    [XRayOptions.LegalLogic]: 'legalLogicEnabled',
    [XRayOptions.Reflow]: 'reflowEnabled',
  };

  constructor() {
    this.initSoftwareUpdater();
  }

  public readonly title = 'semeris';

  private initSoftwareUpdater() {
    this.updateService.init();
  }

  public ngOnInit(): void {
    console.timeEnd(`[AppComponent] ngOnInit`);
    const url = new URL(window.location.href);
    if (url.searchParams.has('iss') || url.searchParams.has('error_description')) {
      this.store.dispatch(AppActions.Logout());
    }

    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.App, id: 'webapp' }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.Reset, id: 'webapp' }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.AssetSetting }));
    this.store.dispatch(AppActions.WebSocketSubscribe({ topic: WebSocketEntities.Label }));

    this.subscription$.add(
      this.route.queryParamMap
        .pipe(
          map((p) => p.get('conversationId')),
          distinctUntilChanged()
        )
        .subscribe((conversationId) => {
          if (conversationId) {
            this.store.dispatch(AppActions.ToggleConversationSidebar({ open: true }));
          }
        })
    );
    this.updateVh();

    this.subscription$.add(
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(250),
          tap(() => {
            this.updateVh();
          })
        )
        .subscribe()
    );

    this.subscription$.add(
      this.store.select(UserProfileSelectors.theme).subscribe((theme) => {
        this.themeService.activateTheme(theme);
      })
    );

    this.subscription$.add(
      this.documentViewerService.sentCommands$
        .pipe(filter((command) => command.commandType === ViewerCommand.SourceReference))
        .subscribe((command) => {
          this.store.dispatch(AppActions.AddActivity({ activity: ActivityEvent.SourceRefNavigate, meta: command }));
        })
    );

    this.subscription$.add(
      this.store
        .select(DocumentViewerSelectors.xRayOptions)
        .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
        .subscribe((options?: XRayOptions[]) => {
          Object.keys(this.xRayOptionsLookup).forEach((option: XRayOptions) => {
            if (options?.includes(option)) {
              this.document.body.classList.add(this.xRayOptionsLookup[option]);
            } else {
              this.document.body.classList.remove(this.xRayOptionsLookup[option]);
            }
          });
        })
    );

    this.isSmall$ = this.store.select(AppSelectors.breakPointId).pipe(
      distinctUntilChanged(),
      map((result) => result === 'small')
    );

    this.breakPointId$ = this.store.select(AppSelectors.breakPointId).pipe(distinctUntilChanged());

    this.store.dispatch(AppActions.StartObservLayout());
    this.subscription$.add(
      combineLatest([
        this.router.events.pipe(
          filter((event) => event instanceof NavigationEnd),
          delay(0),
          tap((event: NavigationEnd) => {
            this.logger.navigation(event.url);
            this.store.dispatch(MenuActions.Navigation({ url: event.url }));
            this.store.dispatch(AppActions.SetUrl({ url: event.url }));
            this.applicationService.sendPanelCommand({
              target: PanelTarget.SearchBox,
              command: PanelCommand.Close,
            });
          })
        ),
        this.permissionService.hasAnyPermission$.pipe(distinctUntilChanged()),
      ]).subscribe(([event, hasAnyPermission]) => {
        if (event.url === '/' || event.urlAfterRedirects === '/') {
          this.router.navigate(hasAnyPermission ? ['/', SemerisRoutes.home.root] : ['/', SemerisRoutes.welcome]);
        }
      })
    );

    this.subscription$.add(
      this.store.select(TitleSelectors.title).subscribe((title) => {
        this.store.dispatch(AppActions.SetTitle({ title }));
      })
    );

    this.initHeartbeat();

    window.addEventListener('offline', () => {
      this.store.dispatch(AppActions.EnterOfflineMode());
    });

    window.addEventListener('online', () => {
      this.store.dispatch(AppActions.RestoreFromOfflineMode());
    });

    this.isAuthenticated$ = this.authService.isAuthenticated$;

    this.authService.error$.pipe(filter((error) => !!error)).subscribe((error) => {
      console.error(error);
      // Auth0 exposes the stable error code on `error.error`; fall back to the human-readable message.
      const errorCode = (error as { error?: string }).error;
      const isLoginRequired = error.message === 'Login required';
      // A silent token refresh can't satisfy an interactive MFA challenge — recover by redirecting to login.
      const isMfaRequired = errorCode === 'mfa_required' || error.message === 'Multifactor authentication required';

      if (isLoginRequired || isMfaRequired) {
        this.authService.loginWithRedirect();
      }
    });

    this.subscription$.add(
      this.authService.isAuthenticated$
        .pipe(
          distinctUntilChanged(),
          withLatestFrom(this.store.select(AppSelectors.isEmbedded)),
          tap(([isAuthenticated, isEmbedded]) => {
            if (isAuthenticated) {
              this.initSessionExpiredNotification();

              if (!isEmbedded) {
                this.store.dispatch(UserProfileActions.LoadProfile({ init: 'welcome' }));
                this.store.dispatch(AppActions.TriggerAppInit());
                this.store.dispatch(HomeActions.StartRefreshToken());
                this.store.dispatch(TransactionActions.StartPollingTransactionChanges());
                this.store.dispatch(DatapointLookupActions.LoadLookup());
              }
            } else {
              this.store.dispatch(HomeActions.StopRefreshToken());
              this.store.dispatch(TransactionActions.StopPollingTransactionChanges());
              this.store.dispatch(AppActions.SetAppEnabled({ appEnabled: true }));
            }
          })
        )
        .subscribe()
    );

    this.subscription$.add(
      combineLatest([this.store.select(AppSelectors.isEmbedded), this.authService.user$])
        .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
        .subscribe(([isEmbedded, userInfo]) => {
          if (isEmbedded && !!userInfo) {
            this.logger.setUser(userInfo.name, userInfo.email);
            this.store.dispatch(
              RootStoreActions.LoadFromLocalStorage({
                email: userInfo.email,
                displayName: userInfo.name,
              })
            );
          }
        })
    );

    this.subscription$.add(
      combineLatest([this.authService.user$, this.store.select(AppSelectors.initialized)])
        .pipe(
          distinctUntilChanged((a, b) => isEqual(a, b)),
          filter(([userInfo, initialized]) => !!userInfo && !!initialized)
        )
        .subscribe(([userInfo]) => {
          this.logger.setUser(userInfo.name, userInfo.email);
          this.store.dispatch(
            RootStoreActions.LoadFromLocalStorage({
              email: userInfo.email,
              displayName: userInfo.name,
            })
          );
        })
    );

    this.subscription$.add(
      this.store
        .select(AppSelectors.initialized)
        .pipe(
          distinctUntilChanged(),
          filter((initialized) => !!initialized),
          withLatestFrom(
            this.store.select(DealFilterSelectors.selectedFilter),
            this.store.select(DealFilterSelectors.list)
          )
        )
        .subscribe(([trigger, dealFilter, list]) => {
          this.store.dispatch(LabelActions.DownloadLabels());
          this.store.dispatch(DealFilterActions.UseDefaultFilter());
        })
    );

    this.permissionService.hasAnyPermission$
      .pipe(
        switchMap((hasAny) =>
          hasAny
            ? this.store.select(AppSelectors.initialized).pipe(
                filter((initialized) => !!initialized),
                take(1)
              )
            : of(true)
        ),
        take(1)
      )
      .subscribe(() => {
        this.document.body.classList.remove('webapp-loading');
        this.document.querySelector('#webapp-loader.loader-frame').remove();
      });

    this.permissionService.permissions$.pipe(distinctUntilChanged((a, b) => isEqual(a, b))).subscribe((permissions) => {
      this.store.dispatch(
        DocumentViewerActions.SetCommentsEnabled({
          enabled: permissions.includes(Permission.WRITE_COMMENT),
        })
      );
    });
  }

  private initSessionExpiredNotification() {
    this.subscription$.add(
      combineLatest([this.authService.isLoading$, this.authService.isAuthenticated$]).subscribe(
        ([isLoading, isAuthenticated]) => {
          if (!isAuthenticated && !window.location.href.includes(SemerisRoutes.auth.logout) && !isLoading) {
            this.store.dispatch(
              AppActions.PushNotification({
                message: `Your session has expired.`,
                action: `Login again`,
                closable: false,
                config: { ...WARNING_NOTIFICATION_CONFIGURATION, duration: 0 },
                id: 'reload_application',
              })
            );
          }
        }
      )
    );
  }

  private initHeartbeat(dueTime?: number) {
    if (this.heartBeatSubscription) {
      return;
    }

    this.heartBeatSubscription = this.dataService
      .checkHeartbeat(dueTime)
      .pipe(
        withLatestFrom(this.store.select(AppSelectors.isOffline)),
        tap(([beat, isOffline]) => {
          if (beat && isOffline) {
            this.store.dispatch(AppActions.RestoreFromOfflineMode());
          }
        })
      )
      .subscribe();
  }

  private pauseHeartbeat() {
    this.heartBeatSubscription?.unsubscribe();
    this.heartBeatSubscription = undefined;
  }

  private updateVh() {
    const vh = window.innerHeight * 0.01;

    this.document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  ngOnDestroy(): void {
    this.store.dispatch(AppActions.StopObservLayout());
    this.subscription$.unsubscribe();
  }
}
